import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PeopleIcon from "@material-ui/icons/People";
import DownloadIcon from "@material-ui/icons/GetApp";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListDialog from "../../components/ContactListDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import planilhaExemplo from "../../assets/planilha.xlsx";
import { AuthContext } from "../../context/Auth/AuthContext";
import MetricsStrip from "../../components/MetricsStrip";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_CONTACTLISTS": {
      const contactLists = action.payload;
      const newContactLists = [];

      contactLists.forEach((contactList) => {
        const contactListIndex = state.findIndex((item) => item.id === contactList.id);

        if (contactListIndex !== -1) {
          state[contactListIndex] = contactList;
        } else {
          newContactLists.push(contactList);
        }
      });

      return [...state, ...newContactLists];
    }
    case "UPDATE_CONTACTLIST": {
      const contactList = action.payload;
      const contactListIndex = state.findIndex((item) => item.id === contactList.id);

      if (contactListIndex !== -1) {
        state[contactListIndex] = contactList;
        return [...state];
      }

      return [contactList, ...state];
    }
    case "DELETE_CONTACTLIST": {
      const contactListId = action.payload;
      return state.filter((item) => item.id !== contactListId);
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  contactCount: {
    fontWeight: 600,
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  loadMoreBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const ContactLists = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);
  const [selectedContactList, setSelectedContactList] = useState(null);
  const [deletingContactList, setDeletingContactList] = useState(null);
  const [contactListModalOpen, setContactListModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [contactLists, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const fetchContactLists = async () => {
        setLoading(true);

        try {
          if (pageNumber === 1) {
            dispatch({ type: "RESET" });
          }

          const { data } = await api.get("/contact-lists/", {
            params: { searchParam, pageNumber },
          });

          dispatch({ type: "LOAD_CONTACTLISTS", payload: data.records || [] });
          setHasMore(Boolean(data.hasMore));
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };

      fetchContactLists();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [pageNumber, reloadFlag, searchParam]);

  useEffect(() => {
    const companyId = user.companyId;

    const onContactListEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTLIST", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACTLIST", payload: +data.id });
      }
    };

    socket.on(`company-${companyId}-ContactList`, onContactListEvent);

    return () => {
      socket.off(`company-${companyId}-ContactList`, onContactListEvent);
    };
  }, [socket, user.companyId]);

  const handleOpenContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(true);
  };

  const handleCloseContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(false);
  };

  const handleSearch = (event) => {
    const newSearchParam = event.target.value.toLowerCase();
    dispatch({ type: "RESET" });
    setSearchParam(newSearchParam);
    setPageNumber(1);
  };

  const handleEditContactList = (contactList) => {
    setSelectedContactList(contactList);
    setContactListModalOpen(true);
  };

  const refreshList = () => {
    setPageNumber(1);
    setReloadFlag((current) => current + 1);
  };

  const handleDeleteContactList = async (contactListId) => {
    try {
      await api.delete(`/contact-lists/${contactListId}`);
      toast.success(i18n.t("contactLists.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }

    setDeletingContactList(null);
    setConfirmModalOpen(false);
    refreshList();
  };

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContacts = (id) => {
    history.push(`/contact-lists/${id}/contacts`);
  };

  const summary = useMemo(() => {
    const totalContacts = contactLists.reduce(
      (total, contactList) => total + Number(contactList.contactsCount || 0),
      0
    );
    const withContacts = contactLists.filter(
      (contactList) => Number(contactList.contactsCount || 0) > 0
    ).length;

    return {
      totalLists: contactLists.length,
      totalContacts,
      withContacts,
      withoutContacts: contactLists.length - withContacts,
    };
  }, [contactLists]);

  const summaryCards = useMemo(
    () => [
      { key: "lists", label: "Listas", value: summary.totalLists },
      { key: "withContacts", label: "Com contatos", value: summary.withContacts },
      { key: "withoutContacts", label: "Sem contatos", value: summary.withoutContacts },
      { key: "contacts", label: "Contatos vinculados", value: summary.totalContacts },
    ],
    [summary]
  );

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={
          deletingContactList &&
          `${i18n.t("contactLists.confirmationModal.deleteTitle")} ${deletingContactList.name}?`
        }
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingContactList(null);
        }}
        onConfirm={() => handleDeleteContactList(deletingContactList.id)}
      >
        {i18n.t("contactLists.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ContactListDialog
        open={contactListModalOpen}
        onClose={handleCloseContactListModal}
        aria-labelledby="form-dialog-title"
        contactListId={selectedContactList && selectedContactList.id}
      />

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              {i18n.t("contactLists.title")}
            </Typography>
          </Box>

          <Box className={classes.heroActions}>
            <Button variant="outlined" onClick={refreshList} disabled={loading}>
              Atualizar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenContactListModal}
            >
              {i18n.t("contactLists.buttons.add")}
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores de listas de contatos" items={summaryCards} />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Listas operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": "Pesquisar listas de contatos" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${summary.totalLists} carregadas`} />
              <Chip size="small" label={`${summary.totalContacts} contatos`} />
            </Box>
          </Box>

          <TableContainer className={classes.tableContainer} onScroll={handleScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("contactLists.table.name")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("contactLists.table.contacts")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("contactLists.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contactLists.map((contactList) => (
                  <TableRow key={contactList.id} hover>
                    <TableCell align="center">{contactList.name}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" className={classes.contactCount}>
                        {contactList.contactsCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className={classes.actionsCell}>
                      <IconButton
                        component="a"
                        href={planilhaExemplo}
                        download="planilha.xlsx"
                        size="small"
                        title="Baixar planilha exemplo"
                      >
                        <DownloadIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        title="Abrir contatos da lista"
                        onClick={() => goToContacts(contactList.id)}
                      >
                        <PeopleIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        title="Editar lista"
                        onClick={() => handleEditContactList(contactList)}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        title="Excluir lista"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingContactList(contactList);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {loading && <TableRowSkeleton columns={3} />}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && contactLists.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography variant="h6">Nenhuma lista encontrada</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Ajuste a busca ou cadastre uma nova lista para continuar.
              </Typography>
            </Box>
          )}

          {!loading && hasMore && contactLists.length > 0 && (
            <div className={classes.loadMoreBox}>
              <Button variant="outlined" color="primary" onClick={loadMore}>
                Carregar mais listas
              </Button>
            </div>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default ContactLists;
